"use client";

import { useLayoutEffect } from "react";
import {
  PORTAL_VISUAL_HEIGHT_VAR,
  PORTAL_VISUAL_TOP_VAR,
  PORTAL_SAFE_TOP_VAR,
  PORTAL_SAFE_BOTTOM_VAR,
  PORTAL_ACTIVE_ATTR,
  DASHBOARD_SAFE_TOP_VAR,
  DEVICE_SAFE_INSET_PROBE_STYLE,
  resolvePortalViewport,
  resolvePortalSafeTop,
} from "@/lib/virtualspace/portal-visual-viewport";

import { isStandaloneDisplayMode } from "./standalone-display-mode";

export {
  PORTAL_VISUAL_HEIGHT_CLASS,
  PORTAL_VISUAL_HEIGHT_VAR,
  PORTAL_VISUAL_TOP_VAR,
} from "@/lib/virtualspace/portal-visual-viewport";

export type SyncPortalVisualViewportOptions = {
  /**
   * When true, never stamp a height shorter than `window.innerHeight`.
   * Used on tab-resume passes: iOS Chrome can report a transient short
   * `visualViewport.height` while the page is already painting full
   * screen (black gap under the hub). Live resize/scroll syncs omit this
   * so URL-bar and keyboard still shrink the shell.
   */
  floorToInnerHeight?: boolean;
  /** Persistent CSS measurement; created once by the hook. */
  probe?: HTMLElement;
};

/**
 * Read the current visual viewport and write CSS vars on `<html>`.
 * Exported for unit tests and for one-shot resume syncs.
 */
export function syncPortalVisualViewport(
  root: HTMLElement = document.documentElement,
  options: SyncPortalVisualViewportOptions = {},
): { height: number; offsetTop: number } {
  if (typeof window === "undefined") {
    return { height: 1, offsetTop: 0 };
  }
  const vv = window.visualViewport;
  const innerHeight = Math.max(1, Math.round(window.innerHeight || 1));
  const probe = options.probe;
  const layoutHeight = probe?.getBoundingClientRect().height || innerHeight;
  const insets = probe ? getComputedStyle(probe) : null;
  const standalone = isStandaloneDisplayMode();
  const active = document.activeElement;
  const editableFocused =
    active instanceof HTMLElement &&
    (active.isContentEditable ||
      (active instanceof HTMLTextAreaElement &&
        !active.readOnly &&
        !active.disabled) ||
      (active instanceof HTMLInputElement &&
        !active.readOnly &&
        !active.disabled &&
        ![
          "button",
          "checkbox",
          "color",
          "file",
          "hidden",
          "radio",
          "range",
          "reset",
          "submit",
        ].includes(active.type)));
  const { height, offsetTop, keyboardOpen } = resolvePortalViewport(
    {
      innerHeight,
      layoutHeight,
      visualHeight: vv?.height ?? innerHeight,
      visualTop: vv?.offsetTop ?? 0,
      scale: vv?.scale ?? 1,
      standalone,
      editableFocused,
    },
    options.floorToInnerHeight,
  );
  stampDeviceSafeTop(root, {
    probe,
    standalone,
    innerHeight,
    visualHeight: vv?.height ?? innerHeight,
    visualTop: vv?.offsetTop ?? 0,
  });
  const safeBottom = keyboardOpen
    ? 0
    : Number.parseFloat(insets?.paddingBottom ?? "0") || 0;
  root.style.setProperty(PORTAL_VISUAL_HEIGHT_VAR, `${height}px`);
  root.style.setProperty(PORTAL_VISUAL_TOP_VAR, `${offsetTop}px`);
  root.style.setProperty(PORTAL_SAFE_BOTTOM_VAR, `${safeBottom}px`);
  return { height, offsetTop };
}

export type StampDeviceSafeTopOptions = {
  probe?: HTMLElement;
  standalone?: boolean;
  innerHeight?: number;
  visualHeight?: number;
  visualTop?: number;
};

/** Write the shared top inset on `<html>` for every installed app shell. */
export function stampDeviceSafeTop(
  root: HTMLElement = document.documentElement,
  options: StampDeviceSafeTopOptions = {},
): number {
  if (typeof window === "undefined") return 0;
  const probe = options.probe;
  const insets = probe ? getComputedStyle(probe) : null;
  const standalone =
    options.standalone ?? isStandaloneDisplayMode();
  const innerHeight = Math.max(
    1,
    Math.round(options.innerHeight ?? window.innerHeight ?? 1),
  );
  const layoutHeight = probe?.getBoundingClientRect().height || innerHeight;
  const safeTop = resolvePortalSafeTop({
    inset: Number.parseFloat(insets?.paddingTop ?? "0") || 0,
    maxInset: Number.parseFloat(insets?.paddingRight ?? "0") || 0,
    standaloneIphone: standalone && /iPhone|iPod/.test(navigator.userAgent),
    layoutHeight,
    innerHeight,
    visualHeight: options.visualHeight ?? window.visualViewport?.height ?? innerHeight,
    visualTop: options.visualTop ?? window.visualViewport?.offsetTop ?? 0,
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
  });
  root.style.setProperty(PORTAL_SAFE_TOP_VAR, `${safeTop}px`);
  root.style.setProperty(DASHBOARD_SAFE_TOP_VAR, `${safeTop}px`);
  return safeTop;
}

function clearPortalVisualViewport(
  root: HTMLElement = document.documentElement,
): void {
  root.style.removeProperty(PORTAL_VISUAL_HEIGHT_VAR);
  root.style.removeProperty(PORTAL_VISUAL_TOP_VAR);
  root.style.removeProperty(PORTAL_SAFE_TOP_VAR);
  root.style.removeProperty(PORTAL_SAFE_BOTTOM_VAR);
}

/**
 * Keep Spatialist portal shells (`h-dvh` / `fixed inset-0`) matched to
 * the *visual* viewport.
 *
 * Field report: iOS Chrome leaves the mission detail (and other portal
 * frames) cut off with empty black below the hub after switching away
 * and back to the tab. CSS `dvh` / `inset-0` can stay stale across
 * that resume; `visualViewport` + visibility/pageshow re-sync forces
 * the shell to the height Chrome is actually painting.
 */
export function usePortalVisualViewport(): void {
  useLayoutEffect(() => {
    const root = document.documentElement;
    const timers: number[] = [];
    const frames = new Set<number>();
    const probe = document.createElement("div");
    probe.setAttribute("data-vs-portal-viewport-probe", "");
    probe.setAttribute("aria-hidden", "true");
    probe.style.cssText = DEVICE_SAFE_INSET_PROBE_STYLE;
    document.body.appendChild(probe);
    root.setAttribute(PORTAL_ACTIVE_ATTR, "");

    const frame = (callback: () => void) => {
      const id = window.requestAnimationFrame(() => {
        frames.delete(id);
        callback();
      });
      frames.add(id);
    };

    const clearDeferred = () => {
      for (const id of timers) window.clearTimeout(id);
      timers.length = 0;
      for (const id of frames) window.cancelAnimationFrame(id);
      frames.clear();
    };

    const syncLive = () => {
      syncPortalVisualViewport(root, { probe });
    };

    const syncResize = () => {
      // Drop pending resume floors so a keyboard/URL-bar shrink isn't
      // overwritten by a stale floored timer from tab resume. Scroll
      // stays unfloored-only — clearing on every vv scroll would cancel
      // resume passes before they can fix a short stamp.
      clearDeferred();
      syncPortalVisualViewport(root, { probe });
    };

    const syncResumePass = () => {
      syncPortalVisualViewport(root, { floorToInnerHeight: true, probe });
    };

    // First paint + deferred passes — iOS Chrome often reports the
    // pre-resume height on the visibilitychange tick and only settles
    // a frame (or hundreds of ms) later. Resume passes floor to
    // innerHeight so a transient short vv cannot leave black below the
    // hub; a final unfloored sync adopts the true vv once Chrome settles.
    const syncSoon = () => {
      clearDeferred();
      syncResumePass();
      if (typeof window.requestAnimationFrame === "function") {
        frame(() => {
          syncResumePass();
          frame(syncResumePass);
        });
      }
      timers.push(window.setTimeout(syncResumePass, 50));
      timers.push(window.setTimeout(syncResumePass, 250));
      timers.push(window.setTimeout(syncResumePass, 500));
      timers.push(window.setTimeout(syncLive, 1000));
    };

    syncSoon();

    const vv = window.visualViewport;
    vv?.addEventListener("resize", syncResize);
    vv?.addEventListener("scroll", syncLive);
    window.addEventListener("resize", syncResize);
    window.addEventListener("orientationchange", syncSoon);
    window.addEventListener("pageshow", syncSoon);
    window.addEventListener("focus", syncSoon);

    const onVisibility = () => {
      if (document.visibilityState === "visible") syncSoon();
    };
    document.addEventListener("visibilitychange", onVisibility);
    document.addEventListener("focusin", syncResize);
    // focusout fires before activeElement settles; avoid treating the old
    // input as a still-open keyboard after tapping outside it.
    const onFocusOut = () => frame(syncLive);
    document.addEventListener("focusout", onFocusOut);
    const displayModes = [
      window.matchMedia("(display-mode: standalone)"),
      window.matchMedia("(display-mode: fullscreen)"),
      window.matchMedia("(display-mode: minimal-ui)"),
    ];
    for (const media of displayModes) media.addEventListener("change", syncSoon);

    return () => {
      clearDeferred();
      vv?.removeEventListener("resize", syncResize);
      vv?.removeEventListener("scroll", syncLive);
      window.removeEventListener("resize", syncResize);
      window.removeEventListener("orientationchange", syncSoon);
      window.removeEventListener("pageshow", syncSoon);
      window.removeEventListener("focus", syncSoon);
      document.removeEventListener("visibilitychange", onVisibility);
      document.removeEventListener("focusin", syncResize);
      document.removeEventListener("focusout", onFocusOut);
      for (const media of displayModes) media.removeEventListener("change", syncSoon);
      probe.remove();
      root.removeAttribute(PORTAL_ACTIVE_ATTR);
      clearPortalVisualViewport(root);
    };
  }, []);
}
